/**
 * The parked L in the live game (M10 P5a11a part 2; P5a10a's live wiring, plan 2026-10-17-m10-p5a follow-up notes).
 * The logic is `./link.ts` (`createParkedLLink`):
 *
 * - **Spawn and release.** When `Career.parkedL` names this system (a launch, a reload, a system entry), the L's
 *   streamed model is ensured first (F8: a reload shows the L's model, never the stand-in), then `spawnParkedL`
 *   puts it at its saved float64 pose as the cradle carrier. It stays while it is parked here or carries the flown
 *   S (`Career.carriedBy`), and is released (`releaseParkedL`, `despawnShip`) on a system change or when neither
 *   holds. `stepParkedL` (P5a10b: hold, or escort the flown S with a hired captain) runs on a control stage.
 * - **The Career writers.** The cradle runtime's `onCarried` goes to the fleet service: the S arrived →
 *   `dockBackToCarrier(id)`; the S left → `relaunchFromCarrier(parkedLRecord(body))` (the L parks where it is).
 * - **Q5.** The relay jump asks `parkedJumpRefusal` on the crossing tick (`travel/runtime/transit.ts
 *   installJumpRefusal`); a parked L is always in open space (no ring mooring for it yet), so it refuses.
 * - **The actions** (`parkedL` service): Unload to the <L>, Take the helm, Stow and Launch from the helm, each gated
 *   (`parkedLActions`) so nothing applies without an owned L in the pair: today's game offers nothing.
 *
 * Gated by data: nothing spawns unless a `Career.parkedL` exists, and no player owns an L until W6.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import { careerAccess } from '../../bridge/m2/career'
import type { Career } from '../../career/types'
import { logger } from '../../core/logger'
import { parkedLCaptained, withParkedOrder } from '../../fleet-link/parkedL'
import type { ReachSession } from '../../reach/session/session'
import { resetCameraRig } from '../../render/cameraRig'
import { installJumpRefusal } from '../../travel/runtime'
import { createParkedLLink, type ParkedLLink, type ParkedLService } from './link'
import { createPairCheckHook, type PairCheckHook } from './pairCheck'
import { actionRefusalReporter, mountParkedLPanel, parkedLHudConfig, parkedLPanelModel, refusalWithToast, stowCandidate, type ParkedLButtonId, type ParkedLPanel, type StowViewShip } from './panel'
import type { M2Runtime } from '../../space/contracts'
import type { CareerService } from '../../career/flow'

/**
 * After the fleet link (`strategic + 2`, the `fleet` service) and after the M1 HUD (`hud`, 500), whose `mountHud` sets
 * `#hud`'s `innerHTML`: a panel mounted under `ctx.hudRoot` before it is detached (P5a11c: at `strategic + 3` the panel
 * never reached the DOM). Every other `hudRoot` mount is ordered after `hud` too.
 */
const ORDER = FEATURE_ORDER.hud + 1

declare module '../../app/services' {
  interface ServiceMap {
    /** M10 P5a11a: the parked L's pair actions (career in the Reach with the fleet link only). */
    parkedL: ParkedLService
  }
}

let link: ParkedLLink | null = null
let offRefusal: (() => void) | null = null
let offAlerts: (() => void) | null = null
let panel: ParkedLPanel | null = null
let refresh: (() => void) | null = null
let pairCheck: PairCheckHook | null = null

const feature: GameFeature = {
  name: 'parkedL',
  order: ORDER,
  init(ctx: GameContext): void {
    const reach = ctx.reach
    if (reach === null) return
    const hud = parkedLHudConfig()
    const m2 = ctx.services.maybe('m2')
    const fleet = ctx.services.maybe('fleet')
    const client = ctx.services.maybe('strategic')
    if (m2 === undefined || fleet === undefined) return
    const l = createParkedLLink({
      sim: m2.space.sim,
      docking: m2.space.docking,
      assets: m2.space.game.assets,
      fleet,
      ships: ctx.services.maybe('ships'),
      career: () => m2.career(),
      systemId: () => (reach.session.ready ? (reach.session.layout?.systemId ?? null) : null),
      // P5a10b: the captain read on spawn (escort needs a hired captain).
      captained: client === undefined ? undefined : (id) => client.fleetView().then((v) => parkedLCaptained(v, id)),
      // P5a11b: the L's loss and the C2 release write through the career runtime's dev command path.
      writeCareer: hasWriteHook(m2) ? (name, cmd) => void m2.applyDevCommand(name, cmd) : undefined,
      // P5a11c: the system's field clumps (the streamer's clusters), read on each system change.
      keepOutOf: () => ('streamer' in reach.session ? (reach.session as ReachSession).streamer.clusterSpheres() : null),
      onSwap: () => resetCameraRig(ctx.rig),
      launchClearM: hud.launchClearM,
    })
    link = l
    // P5a11c: the refusal is a HUD toast as well as the transit log.
    offRefusal = installJumpRefusal(refusalWithToast((id) => l.refusal(id), (text) => panel?.toast(text, hud.toastS)))
    // P5a11c: the captain re-read on a fleet-view change (a fleet alert: hire, strike, recovery).
    offAlerts = client?.onFleetAlert(() => l.recheckCaptain()) ?? null
    ctx.services.provide('parkedL', l.service)

    const access = hasWriteHook(m2) ? careerAccess(m2) : null
    // D-TBD (H-1 follow-up): the Career/worker pair check, queued on each worker re-init from a save.
    if (client !== undefined && access !== null) {
      pairCheck = createPairCheckHook(
        {
          fleetView: () => client.fleetView(),
          command: (cmd) => client.command(cmd),
          career: () => m2.career(),
          write: (name, cmd) => {
            const r = access.apply(name, cmd)
            return r.ok ? { ok: true } : { ok: false, code: r.error.code }
          },
        },
        hud.pairCheckRetries,
        // Dynamic, as the Charter career bridge does: the strategic link's module stays out of this chunk.
        () => import('../../strategic-link/service').then(({ strategicBoot }) => strategicBoot()),
      )
    }
    let stowShips: readonly StowViewShip[] | null = null
    let fetchingStow = false
    const nameOf = (career: Career, fleetId: string): string => {
      const e = (career.hangar ?? []).find((h) => h.fleetId === fleetId)
      return e?.name ?? ctx.sim.defs.find((d) => d.id === e?.defId)?.name ?? 'carrier'
    }
    // D-TBD (H-1 follow-up): a refused pair action is a HUD toast as well as the log line.
    const done = actionRefusalReporter(hud, (text) => panel?.toast(text, hud.toastS))
    const press = (id: ParkedLButtonId): void => {
      const s = l.service
      if (id === 'unload') done('unload', 'Unload')(s.unload())
      else if (id === 'takeHelm') void s.takeHelm()?.then(done('take the helm', 'Take the helm'))
      else if (id === 'launch') void s.launchFromHelm()?.then(done('launch', 'Launch'))
      else if (id === 'stow') {
        const c = stowCandidate(m2.career(), stowShips)
        if (c !== null) void s.stow(c.shipId, c.stationId)?.then(done('stow', 'Stow'))
      } else {
        const order = id === 'order-escort' ? 'escort' : 'hold'
        if (access === null) return
        const r = access.apply('parkedL order', (c) => {
          const w = withParkedOrder(c, order, l.captained())
          if (!w.ok) throw new Error(w.reason)
          return w.career
        })
        if (!r.ok) done(`order ${order}`, order === 'escort' ? 'Order: escort' : 'Order: hold')({ ok: false, error: r.error.code })
      }
    }
    const p = mountParkedLPanel(ctx.hudRoot, press)
    panel = p
    let last: Career | null = null
    let lastCaptained = false
    refresh = (): void => {
      const career = m2.career()
      const captained = l.captained()
      if (career === last && captained === lastCaptained) return
      last = career
      lastCaptained = captained
      const actions = l.service.actions()
      // Stow reads the worker fleet view (where the fleet ships are docked): fetched at event rate, then re-rendered.
      if (actions.stow && client !== undefined && !fetchingStow) {
        fetchingStow = true
        client.fleetView().then(
          (v) => {
            fetchingStow = false
            stowShips = v.ships
            const now = m2.career()
            p.render(parkedLPanelModel({ career: now, actions: l.service.actions(), captained: l.captained(), nameOf: (id) => nameOf(now, id), stow: stowCandidate(now, stowShips) }))
          },
          (err: unknown) => {
            fetchingStow = false
            logger.warn('parkedL: fleet view for Stow failed', err)
          },
        )
      }
      p.render(parkedLPanelModel({ career, actions, captained, nameOf: (id) => nameOf(career, id), stow: stowCandidate(career, stowShips) }))
    }
  },
  frame(): void {
    link?.sync()
    refresh?.()
    pairCheck?.frame()
  },
  dispose(): void {
    pairCheck?.dispose()
    pairCheck = null
    offRefusal?.()
    offRefusal = null
    offAlerts?.()
    offAlerts = null
    panel?.dispose()
    panel = null
    refresh = null
    link?.dispose()
    link = null
  },
}

function hasWriteHook(m2: M2Runtime): m2 is CareerService {
  return 'applyDevCommand' in m2 && typeof m2.applyDevCommand === 'function'
}

export default feature
